import { formatDate } from '@util/date-util'

import type { AuthenticatedUser } from '../../../auth/types/AuthenticatedUser'
import type { Subproces } from '../types/Subproces'

import {
  type StepConfig,
  canEditSubproces,
  generateStepStatuses,
  getPausedCaptionForStatus,
  getStatusLabelForType,
} from './subproces-util'

export {
  canEditSchouw,
  generateSchouwStepStatuses,
  getSchouwPlannenCaption,
  getSchouwBeoordelenCaption,
  getSchouwDoneCaption,
}

const SCHOUW_STEPS = [
  { status: 'created' },
  { status: 'planned' },
  { status: 'done' },
] as const satisfies readonly StepConfig[]

/**
 * Een schouw mag bewerkt worden door de combi óf de organisatie die op het
 * subproces is toegewezen (de aannemer).
 */
const canEditSchouw = ({
  user,
  subproces,
}: {
  user: AuthenticatedUser
  subproces: Subproces
}) => canEditSubproces({ user, subproces, mode: 'or' })

/**
 * Een afgekeurde schouw kent geen eigen stap: hij keert terug naar de eerste
 * stap ('plannen') zodat de schouw opnieuw ingepland kan worden. De indicatie
 * dat er afgekeurd is, wordt in de beoordelen-stap zelf getoond.
 */
const generateSchouwStepStatuses = ({
  subproces,
}: {
  subproces: Subproces
}) => {
  const effectiveSubproces =
    subproces.status === 'rejected'
      ? { ...subproces, status: 'created' as const }
      : subproces

  return generateStepStatuses({
    subproces: effectiveSubproces,
    steps: SCHOUW_STEPS,
  })
}

/**
 * Onderschrift van de plannen-stap: toont de pauze-informatie wanneer het
 * proces vanuit deze stap gepauzeerd is.
 */
const getSchouwPlannenCaption = ({ subproces }: { subproces: Subproces }) =>
  getPausedCaptionForStatus({ subproces, status: 'created' })

/**
 * Onderschrift van de beoordelen-stap. De afkeur-indicatie blijft altijd
 * zichtbaar op deze stap, ook als het proces gepauzeerd is; dan tonen we beide
 * in het onderschrift.
 */
const getSchouwBeoordelenCaption = ({
  subproces,
}: {
  subproces: Subproces
}) => {
  const rejectionCaption =
    subproces.rejectionCount > 0
      ? `${subproces.rejectionCount} keer afgekeurd`
      : undefined

  const pausedInformation = getPausedCaptionForStatus({
    subproces,
    status: 'planned',
  })

  return (
    [pausedInformation, rejectionCaption].filter(Boolean).join(' - ') ||
    undefined
  )
}

/**
 * Onderschrift van de afgeronde stap: toont wanneer en door wie de schouw is
 * uitgevoerd/goedgekeurd.
 */
const getSchouwDoneCaption = ({ subproces }: { subproces: Subproces }) => {
  if (subproces.status !== 'done' || !subproces.completed) return undefined

  return subproces.completed.schouwDatum
    ? `Schouw uitgevoerd op ${formatDate(subproces.completed.schouwDatum)}`
    : `${getStatusLabelForType('schouw', 'done')} op ${formatDate(
        subproces.completed.date,
      )} door ${subproces.completed.organisatie}`
}
